import React, { useEffect, useState } from 'react';

import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import PaginationWithDropdown from 'src/components/shared/paginationWithDropdown/paginationWithDropdown';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import shiftsService from 'src/services/shiftsService';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import {
  getShifts,
  setCurrentPage,
  setPerPage,
  setSearchField,
  type TableDataRow,
} from 'src/store/updateShiftsPageSlice';

import './updateShifts.scss';

import { DeleteShiftModal } from './shiftsModal/deleteShiftModal';
import ShiftsModal from './shiftsModal/shiftModal';
import ShiftsTable from './shiftsTable/shiftsTable';
import TopOptions from './topOptions/topOptions';

const UpdateShifts = () => {
  const dispatch = useAppDispatch();
  const { isLoading, currentPage, totalPages, perPage } = useAppSelector((state) => state.updateShiftsPage);
  const { preferredSite } = useAppSelector((state) => state.user);
  const { userLoading } = useAppSelector((state) => state.user);

  // Get shifts implementation, redux cleanup hook
  useEffect(() => {
    if (userLoading === 'fulfilled' && preferredSite !== 0) {
      dispatch(getShifts());
    }
    return () => {
      dispatch(setSearchField(''));
    };
  }, [userLoading, preferredSite, dispatch]);

  // Delete shift implementation
  const [shiftToDelete, setShiftToDelete] = useState<TableDataRow>();

  const deleteShift = async () => {
    const response = await shiftsService.deleteShift(shiftToDelete!.id);
    if (response.status === 200) {
      dispatch(
        createToast({
          type: 'success',
          message: `Deleted shift ${shiftToDelete?.shiftCode}`,
          timeout: TOAST_TIMEOUT,
        }),
      );
      setShiftToDelete(undefined);
      dispatch(getShifts());
    }
    // utils/apiClient.ts has an interceptor to toast errors, no need to do it here
  };

  const deleteShiftModalProps = {
    deletionTarget: shiftToDelete,
    isOpen: !!shiftToDelete,
    onCancelDelete: () => setShiftToDelete(undefined),
    onConfirmDelete: deleteShift,
  };

  // Display logic
  return (
    <div className="update-shifts">
      {!isLoading ? (
        <>
          <Heading level={1} type="h500" className="update-shifts__page-heading">
            Update Shifts
          </Heading>
          <Text>Here you view, and edit shifts available at your site</Text>
          <TopOptions />
          <ShiftsTable onDeleteShift={(shift) => setShiftToDelete(shift)} />
          <PaginationWithDropdown
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            setPerPage={(newPerPage) => dispatch(setPerPage(newPerPage))}
            setCurrentPage={(newPage) => dispatch(setCurrentPage(newPage))}
          />
          <ShiftsModal />
          <DeleteShiftModal {...deleteShiftModalProps} />
        </>
      ) : (
        <LoadingSpinner />
      )}
    </div>
  );
};

export default UpdateShifts;
